import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, Send, ShieldCheck, AlertTriangle, CheckCircle2, Mail, FileText, Receipt,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { createRefundRequest } from "@/lib/api";

const POLICY_BULLETS = [
  "Refund requests must be submitted within 24 hours of payment.",
  "Urgent, Priority, and Same-Day services are non-refundable.",
  "Approved refunds are issued to the original payment method via Stripe (5–10 business days).",
  "You must use the same email address used at booking.",
];

export default function RefundRequest() {
  const [form, setForm] = useState({
    lead_id: "",
    invoice_number: "",
    customer_email: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.lead_id || !form.invoice_number || !form.customer_email) {
      toast.error("Please complete all required fields.");
      return;
    }
    try {
      setSubmitting(true);
      const r = await createRefundRequest({
        lead_id: form.lead_id.trim(),
        invoice_number: form.invoice_number.trim(),
        customer_email: form.customer_email.trim(),
        reason: form.reason.trim() || undefined,
      });
      setResult(r);
      if (r.ok) {
        toast.success("Refund request received.");
      } else {
        toast.error(r.message || "Could not verify your request.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ===========================================================================
  // Result screen
  // ===========================================================================
  if (result) {
    const ok = !!result.ok;
    const isPolicyReject = result.status === "not_eligible";
    return (
      <div className="min-h-screen" style={{ background: "#F8FAFC" }}>
        <TopBar />
        <main className="max-w-2xl mx-auto px-5 py-12">
          <div
            className="rounded-2xl border bg-white p-8 sm:p-12"
            style={{ borderColor: "#E2E8F0" }}
            data-testid="refund-result-card"
          >
            <div
              className="mx-auto mb-6 grid place-items-center h-16 w-16 rounded-2xl"
              style={{
                background: ok ? "rgba(16,185,129,0.12)" : "rgba(220,38,38,0.1)",
                color: ok ? "#065F46" : "#991B1B",
              }}
            >
              {ok ? <CheckCircle2 className="h-9 w-9" /> : <AlertTriangle className="h-9 w-9" />}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-center" style={{ color: "#0B2545" }}>
              {ok ? "Refund request received." : (isPolicyReject ? "Not eligible for refund." : "Could not verify request.")}
            </h1>
            <p className="mt-3 text-center text-sm max-w-md mx-auto" style={{ color: "#475569" }}>
              {result.message}
            </p>
            {ok ? (
              <p className="mt-2 text-center text-xs" style={{ color: "#94A3B8" }}>
                Reference: <span className="font-mono">{result.id?.slice(0, 8)}</span>
              </p>
            ) : null}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button variant="outline" className="h-11" data-testid="refund-back-home">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Return to home
                </Button>
              </Link>
              {!ok ? (
                <Button
                  onClick={() => setResult(null)}
                  className="h-11"
                  style={{ background: "#0B2545", color: "#fff" }}
                  data-testid="refund-try-again"
                >
                  Submit a different request
                </Button>
              ) : null}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ===========================================================================
  // Form
  // ===========================================================================
  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }}>
      <TopBar />
      <main className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
        <div className="mb-7">
          <div className="text-[10px] uppercase tracking-[0.22em] font-medium" style={{ color: "#7A5E12" }}>
            T&amp;J Clinical Solutions
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold" style={{ color: "#0B2545" }}>
            Refund request
          </h1>
          <p className="mt-2 text-sm max-w-xl" style={{ color: "#475569" }}>
            Please provide your booking details below. Our team will verify your request and process eligible refunds via Stripe to your original payment method.
          </p>
        </div>

        {/* Policy notice */}
        <div
          className="mb-6 rounded-xl border p-4 sm:p-5"
          style={{ borderColor: "rgba(212,175,55,0.4)", background: "rgba(212,175,55,0.08)" }}
          data-testid="refund-policy-card"
        >
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#7A5E12" }} />
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#7A5E12" }}>
                Refund policy
              </div>
              <ul className="mt-2 space-y-1.5 text-[13px]" style={{ color: "#0B2545" }}>
                {POLICY_BULLETS.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ background: "#7A5E12" }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-2xl border bg-white p-6 sm:p-8 space-y-5"
          style={{ borderColor: "#E2E8F0" }}
          data-testid="refund-form"
        >
          <Field
            icon={Receipt}
            label="Lead ID"
            help="Format: TJC-#### (you'll find it in your booking confirmation or invoice)."
            required
          >
            <Input
              value={form.lead_id}
              onChange={(e) => update("lead_id", e.target.value)}
              placeholder="TJC-1234"
              className="h-11"
              required
              data-testid="refund-input-lead-id"
            />
          </Field>

          <Field
            icon={FileText}
            label="Invoice number"
            help="Found on the PDF invoice we emailed after your payment."
            required
          >
            <Input
              value={form.invoice_number}
              onChange={(e) => update("invoice_number", e.target.value)}
              placeholder="INV-TJC-1234-01"
              className="h-11"
              required
              data-testid="refund-input-invoice"
            />
          </Field>

          <Field
            icon={Mail}
            label="Email address used for booking"
            required
          >
            <Input
              type="email"
              value={form.customer_email}
              onChange={(e) => update("customer_email", e.target.value)}
              placeholder="you@example.com"
              className="h-11"
              required
              data-testid="refund-input-email"
            />
          </Field>

          <Field label="Reason for refund (optional)">
            <Textarea
              value={form.reason}
              onChange={(e) => update("reason", e.target.value)}
              placeholder="Briefly describe what happened…"
              className="min-h-[100px]"
              data-testid="refund-input-reason"
            />
          </Field>

          <div className="pt-2 flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:justify-between">
            <Link to="/" className="text-xs underline" style={{ color: "#0B2545" }} data-testid="refund-cancel-link">
              Cancel
            </Link>
            <Button
              type="submit"
              disabled={submitting}
              className="h-12 px-6 text-sm font-semibold"
              style={{ background: "#0B2545", color: "#fff" }}
              data-testid="refund-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Submit refund request
            </Button>
          </div>
        </form>

        <p className="mt-6 text-center text-[11px]" style={{ color: "#94A3B8" }}>
          Need help? Email <a className="underline" href="mailto:tj.clinicalsolutions@gmail.com">tj.clinicalsolutions@gmail.com</a>.
        </p>
      </main>
    </div>
  );
}

const TopBar = () => (
  <header className="border-b bg-white" style={{ borderColor: "#E2E8F0" }}>
    <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#0B2545" }}>
        <ArrowLeft className="h-4 w-4" />
        Back to T&amp;J Clinical Solutions
      </Link>
    </div>
  </header>
);

const Field = ({ icon: Icon, label, help, required, children }) => (
  <div>
    <Label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-medium" style={{ color: "#475569" }}>
      {Icon ? <Icon className="h-3 w-3" style={{ color: "#7A5E12" }} /> : null}
      {label} {required ? <span style={{ color: "#991B1B" }}>*</span> : null}
    </Label>
    <div className="mt-1.5">{children}</div>
    {help ? <p className="mt-1 text-[11px]" style={{ color: "#94A3B8" }}>{help}</p> : null}
  </div>
);
